import React, { useContext } from "react"
import { ExportHierarchyContext } from "./Exports"
import * as Yup from "yup"
import { IExportHierarchyPrimeRequestModel } from "./models"
import { Form, Formik, FormikHelpers } from "formik"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { getExportHierarchyPrime } from "./requests"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"

const ExportHierarchyPrime = () => {
     const context = useContext(ExportHierarchyContext)

     const schema = Yup.object().shape({
          dateFrom: Yup.string().label("`Date de début`").required(),
          dateTo: Yup.string().label("`Date de fin`").required(),
     })

     const initialValues: IExportHierarchyPrimeRequestModel = {}

     function handleSubmit(values: IExportHierarchyPrimeRequestModel, helpers: FormikHelpers<IExportHierarchyPrimeRequestModel>) {
          helpers.setStatus(null)

          toast.info("Génération en cours ...", {
               autoClose: false,
          })

          getExportHierarchyPrime(values)
               .then(r => {
                    const fileName = `export_hierarchie_prime.csv`
                    FileAndDriveHelper.downloadFile(r.data, fileName)

                    toast.dismiss()
                    toast.success("L'export a bien été généré")
               })
               .catch(async (e: AxiosError) => {
                    let error: IHttpErrorResponseModel | undefined

                    // Check if we got a blob (file-like response)
                    if (e.response?.data instanceof Blob) {
                         try {
                              const text = await e.response.data.text()
                              error = JSON.parse(text)
                         } catch {
                              // fallback in case JSON parsing fails
                              error = { detail: "Une erreur inconnue est survenue." } as IHttpErrorResponseModel
                         }
                    } else {
                         error = e.response?.data
                    }

                    console.log("Parsed error:", error)

                    // Set form errors if validation
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set global status & toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail ?? "Erreur lors de l’export", { autoClose: false })

                    helpers.setSubmitting(false)
               })
               .finally(() => helpers.setSubmitting(false))
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
               {helpers => {
                    return (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              <div className="row mb-4">
                                   <label className="col-lg-4 col-form-label fw-bold fs-6">Période</label>

                                   <div className="col-lg-8">
                                        <div className="d-flex">
                                             <MyDateDayMonthYearField
                                                  name={"dateFrom"}
                                                  value={helpers.values.dateFrom}
                                                  placeholder={"Du"}
                                                  onChange={val => helpers.setFieldValue("dateFrom", val)}
                                                  isInvalid={!!helpers.errors.dateFrom}
                                                  maxDate={helpers.values.dateTo}
                                                  classNames={"rounded-0 rounded-start"}
                                                  autofocus
                                             />
                                             <span className="input-group-text fas fa-ellipsis d-flex align-items-center rounded-0" />
                                             <MyDateDayMonthYearField
                                                  name={"dateTo"}
                                                  value={helpers.values.dateTo}
                                                  placeholder={"Au"}
                                                  onChange={val => helpers.setFieldValue("dateTo", val)}
                                                  isInvalid={!!helpers.errors.dateTo}
                                                  minDate={helpers.values.dateFrom}
                                                  classNames={"rounded-0 rounded-end"}
                                             />
                                        </div>
                                        {helpers.errors.dateFrom && <div className={"text-danger"}>{helpers.errors.dateFrom}</div>}
                                        {helpers.errors.dateTo && <div className={"text-danger"}>{helpers.errors.dateTo}</div>}
                                   </div>
                              </div>

                              <div className="separator bg-primary my-5" />

                              {/* Submit button */}
                              <div className={"d-flex justify-content-end"}>
                                   <button
                                        className={"btn btn-sm btn-outline btn-outline-primary d-inline-flex align-items-center me-2"}
                                        type={"button"}
                                        disabled={helpers.isSubmitting}
                                        onClick={() => context.setSelectedType(null)}
                                   >
                                        <span className={"d-none d-lg-block"}>Annuler</span>
                                   </button>
                                   <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                        {!helpers.isSubmitting ? (
                                             <>
                                                  Télécharger <span className={"fas fa-download fs-4 ms-2"} />
                                             </>
                                        ) : (
                                             <MySimpleSpinner size={"sm"} />
                                        )}
                                   </button>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default ExportHierarchyPrime
